/**
 * Feature E02-F05 — "Kick-off вопросник (Stage 2/3)" request/response contracts.
 *
 * 1:1 port of the legacy n8n `manage_kickoff_questions` (group KQ) read branch and
 * the `update_project` kickoff-data merge, plus the front-end questionnaire model
 * that lived in `public/assets/js/project-kickoff.js`:
 *
 *   - GET  /admin/kickoff-questions   → KQ `get_questions` (active, sorted)   (T02)
 *   - PATCH /projects/:id (updates.kickoff_*)  → answer/notes/transcript merge (T06)
 *
 * Source: docs/source-n8n/reference/KQ.md, Kickoff.md;
 *         ../EER app/public/assets/js/project-kickoff.js;
 *         ../EER app/docs/migrations/019-kickoff-questions.sql.
 *
 * Lives in its OWN file (NOT re-exported from the shared barrel by this agent);
 * the serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Reference question (KQ get_questions row) — T02
// ---------------------------------------------------------------------------
/**
 * A row of the `kickoff_questions` reference table, exactly the projection the
 * legacy KQ `Get All` node returned (SELECT id, category, text, ai_instruction,
 * sort_order, is_active WHERE is_active=true ORDER BY sort_order, id).
 * `ai_instruction` is a content hint for the prefill-AI, NOT an LLM call here.
 */
export const kickoffQuestionRowSchema = z.object({
    id: z.number().int(),
    category: z.string(),
    text: z.string(),
    ai_instruction: z.string().default(''),
    sort_order: z.number().int().default(0),
    is_active: z.boolean().default(true),
});
/**
 * GET /admin/kickoff-questions success body — `{ success:true, questions:[...] }`
 * (legacy `{ response:{ success:true, questions } }`, the `response` wrapper is
 * unwrapped by the transport). Only active questions, ORDER BY sort_order.
 */
export const kickoffQuestionsListResultSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    questions: z.array(kickoffQuestionRowSchema),
});
// ---------------------------------------------------------------------------
// Front-end questionnaire item (template + merged) — T03/T05
// ---------------------------------------------------------------------------
/**
 * One questionnaire item as the SPA manipulates it: a reference question carrying
 * the recruiter's answer + a `done` flag. This is the shape stored in
 * `projects.data.kickoff_questions` (the ANSWER source) and the shape the merged
 * list is built into (`_buildMergedKickoffQuestions`). `text` is editable inline.
 */
export const kickoffQuestionItemSchema = z.object({
    category: z.string(),
    text: z.string(),
    answer: z.string().default(''),
    done: z.boolean().default(false),
});
/**
 * A position-type extra question (`position_types.questions` jsonb element). The
 * legacy node stores `{ text, category }`; `category` defaults to
 * 'Position-Specific' when absent (parity with `_buildMergedKickoffQuestions`).
 *
 * DISTINCT from the admin-side `positionTypeQuestionSchema`/`PositionTypeQuestion`
 * in `admin-questionnaire.ts` (which has a REQUIRED `category` defaulted via the
 * admin save flow, no passthrough). This kickoff-merge variant is permissive
 * (`category` optional + `.passthrough()` to survive a free-form jsonb blob), so
 * it carries its OWN name — the collision is resolved by renaming, not merging.
 */
export const kickoffPositionTypeQuestionSchema = z
    .object({
    text: z.string(),
    category: z.string().optional(),
})
    .passthrough();
// ---------------------------------------------------------------------------
// Save payload (update_project branch-4 arbitrary updates) — T06
// ---------------------------------------------------------------------------
/**
 * The `updates` bag the questionnaire sends to PATCH /projects/:id. Every key
 * merges into `projects.data` (NOT a whole-data overwrite) — parity with the
 * legacy `update_project` branch-4 jsonb merge. `meeting_completed` is only sent
 * by Complete Meeting. Mirrors the subset of `projectUpdatesSchema` this feature
 * owns, kept here so the questionnaire client and its tests share ONE shape.
 */
export const kickoffSaveUpdatesSchema = z.object({
    kickoff_notes: z.string().default(''),
    kickoff_transcript: z.string().default(''),
    kickoff_questions: z.array(kickoffQuestionItemSchema),
    meeting_completed: z.boolean().optional(),
});
// ---------------------------------------------------------------------------
// Merged questionnaire result (GET /projects/:id/kickoff-questions) — T05
// ---------------------------------------------------------------------------
/**
 * GET /projects/:id/kickoff-questions success body. The merged questionnaire
 * (`_buildMergedKickoffQuestions`): the active base template + the project's
 * position-type extra questions, with the project's saved answers carried over by
 * text-match, legacy saved questions appended last. The SPA renders this list and
 * treats `data.kickoff_questions` strictly as the answer source — the list is
 * ALWAYS rebuilt here, never read as canonical (T05 acceptance #4).
 */
export const mergedKickoffQuestionsResultSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    questions: z.array(kickoffQuestionItemSchema),
});
/** Category display order used by the UI when a category icon/sort is needed. */
export const KICKOFF_CATEGORY_ORDER = [
    'Getting to Know the Client',
    'The Ideal Candidate',
    'About the Position',
    'Building the Need',
    'For Sourcing Use',
    'Position-Specific',
];
//# sourceMappingURL=kickoff-questionnaire.js.map